import type { SupportedLocale } from '@erp/shared';

const de = {
  title: 'Zugriff auf frühere Postfachstände',
  description:
    'Frühere private Nachrichten werden neuen Mitarbeitern nicht automatisch freigegeben.',
  empty: 'Keine früheren Postfachstände vorhanden.',
  loadFailed: 'Die früheren Postfachstände konnten nicht geladen werden.',
  editAction: 'Zugriff bearbeiten: {{address}}',
  editorTitle: 'Historischen Postfachzugriff bearbeiten',
  employee: 'Mitarbeiter',
  readFor: 'Frühere Nachrichten lesen: {{name}}',
  editFor: 'Frühere Nachrichten bearbeiten: {{name}}',
  bccFor: 'Frühere BCC lesen: {{name}}',
  read: 'Lesen',
  edit: 'Bearbeiten',
  bcc: 'BCC lesen',
  rightsHint:
    'Zusätzlich bleiben die aktuellen Postfachrechte und passenden Rollenrechte erforderlich. Verwaltung allein erlaubt kein Lesen.',
  reason: 'Begründung',
  reasonHint: 'Dokumentiere, warum dieser historische Zugriff geändert wird.',
  checkFields: 'Bitte prüfe die markierten Angaben.',
  reasonRequired: 'Bitte gib eine Begründung mit mindestens drei Zeichen ein.',
  save: 'Freigabe speichern',
  saved: 'Historische Freigabe gespeichert',
  saveFailed: 'Die Freigabe konnte nicht gespeichert werden. Deine Angaben bleiben erhalten.',
  memberUnavailable:
    'Aktiviere nur Rechte, die der Mitarbeiter auch im aktuellen Postfach besitzt.',
  duplicateMember: 'Ein Mitarbeiter wurde mehrfach angegeben.',
};
type HistoryTexts = { [K in keyof typeof de]: string };
export const centralMailHistoryTranslations = {
  de,
  en: {
    title: 'Access to previous mailbox identities',
    description: 'Previous private messages are not automatically shared with new employees.',
    empty: 'No previous mailbox identities.',
    loadFailed: 'Previous mailbox identities could not be loaded.',
    editAction: 'Edit access: {{address}}',
    editorTitle: 'Edit historical mailbox access',
    employee: 'Employee',
    readFor: 'Read previous messages: {{name}}',
    editFor: 'Edit previous messages: {{name}}',
    bccFor: 'Read previous BCC: {{name}}',
    read: 'Read',
    edit: 'Edit',
    bcc: 'Read BCC',
    rightsHint:
      'Current mailbox and role permissions remain required. Administration alone does not allow reading.',
    reason: 'Reason',
    reasonHint: 'Explain why this historical access is being changed.',
    checkFields: 'Please check the highlighted fields.',
    reasonRequired: 'Enter a reason of at least three characters.',
    save: 'Save access',
    saved: 'Historical access saved',
    saveFailed: 'Access could not be saved. Your entries are retained.',
    memberUnavailable: 'Enable only permissions the employee also has in the current mailbox.',
    duplicateMember: 'An employee was specified more than once.',
  },
  fr: {
    title: 'Accès aux anciennes identités de boîte',
    description:
      'Les anciens messages privés ne sont pas partagés automatiquement avec les nouveaux collaborateurs.',
    empty: 'Aucune ancienne identité de boîte.',
    loadFailed: 'Impossible de charger les anciennes identités de boîte.',
    editAction: 'Modifier l’accès : {{address}}',
    editorTitle: 'Modifier l’accès historique à la boîte',
    employee: 'Collaborateur',
    readFor: 'Lire les anciens messages : {{name}}',
    editFor: 'Modifier les anciens messages : {{name}}',
    bccFor: 'Lire les anciennes CCI : {{name}}',
    read: 'Lire',
    edit: 'Modifier',
    bcc: 'Lire les CCI',
    rightsHint:
      'Les droits actuels de boîte et de rôle restent nécessaires. L’administration seule ne permet pas de lire.',
    reason: 'Motif',
    reasonHint: 'Expliquez pourquoi cet accès historique est modifié.',
    checkFields: 'Vérifiez les champs signalés.',
    reasonRequired: 'Saisissez un motif d’au moins trois caractères.',
    save: 'Enregistrer l’accès',
    saved: 'Accès historique enregistré',
    saveFailed: 'Impossible d’enregistrer l’accès. Vos saisies sont conservées.',
    memberUnavailable:
      'Activez uniquement les droits que le collaborateur possède dans la boîte actuelle.',
    duplicateMember: 'Un collaborateur apparaît plusieurs fois.',
  },
  es: {
    title: 'Acceso a identidades anteriores del buzón',
    description:
      'Los mensajes privados anteriores no se comparten automáticamente con empleados nuevos.',
    empty: 'No hay identidades anteriores del buzón.',
    loadFailed: 'No se pudieron cargar las identidades anteriores del buzón.',
    editAction: 'Editar acceso: {{address}}',
    editorTitle: 'Editar acceso histórico al buzón',
    employee: 'Empleado',
    readFor: 'Leer mensajes anteriores: {{name}}',
    editFor: 'Editar mensajes anteriores: {{name}}',
    bccFor: 'Leer CCO anteriores: {{name}}',
    read: 'Leer',
    edit: 'Editar',
    bcc: 'Leer CCO',
    rightsHint:
      'Se siguen requiriendo los permisos actuales del buzón y del rol. Administrar no permite leer por sí solo.',
    reason: 'Motivo',
    reasonHint: 'Explica por qué se cambia este acceso histórico.',
    checkFields: 'Revisa los campos indicados.',
    reasonRequired: 'Introduce un motivo de al menos tres caracteres.',
    save: 'Guardar acceso',
    saved: 'Acceso histórico guardado',
    saveFailed: 'No se pudo guardar el acceso. Se conservan los datos introducidos.',
    memberUnavailable: 'Activa solo permisos que el empleado también tenga en el buzón actual.',
    duplicateMember: 'Un empleado aparece más de una vez.',
  },
  tr: {
    title: 'Önceki posta kutusu kimliklerine erişim',
    description: 'Önceki özel iletiler yeni çalışanlarla otomatik paylaşılmaz.',
    empty: 'Önceki posta kutusu kimliği yok.',
    loadFailed: 'Önceki posta kutusu kimlikleri yüklenemedi.',
    editAction: 'Erişimi düzenle: {{address}}',
    editorTitle: 'Geçmiş posta kutusu erişimini düzenle',
    employee: 'Çalışan',
    readFor: 'Önceki iletileri oku: {{name}}',
    editFor: 'Önceki iletileri düzenle: {{name}}',
    bccFor: 'Önceki gizli alıcıları oku: {{name}}',
    read: 'Oku',
    edit: 'Düzenle',
    bcc: 'Gizli alıcıları oku',
    rightsHint:
      'Güncel posta kutusu ve rol izinleri de gereklidir. Yalnızca yönetim yetkisi okumaya izin vermez.',
    reason: 'Gerekçe',
    reasonHint: 'Bu geçmiş erişimin neden değiştirildiğini açıklayın.',
    checkFields: 'İşaretli alanları kontrol edin.',
    reasonRequired: 'En az üç karakterlik bir gerekçe girin.',
    save: 'Erişimi kaydet',
    saved: 'Geçmiş erişim kaydedildi',
    saveFailed: 'Erişim kaydedilemedi. Girdileriniz korunur.',
    memberUnavailable:
      'Yalnızca çalışanın güncel posta kutusunda da sahip olduğu izinleri etkinleştirin.',
    duplicateMember: 'Bir çalışan birden fazla kez belirtildi.',
  },
} satisfies Record<SupportedLocale, HistoryTexts>;
